import { useState } from "react";
import { Users, TrendingUp, PieChart, Lightbulb } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { CredorStat } from "../chartUtils";
import { ModalEntityTitle, OutcomeTable, SummaryBanner } from "./FinanceiroModalContent";
import { cn } from "@/utils/cn";

/* ─── Medalhas / cores por posição ───────────────────────────────────────── */
const RANK_COLORS = [
  { bar: "#f59e0b", text: "text-amber-500",   badge: "bg-amber-400 text-white",    ring: "ring-amber-300" },
  { bar: "#94a3b8", text: "text-slate-500",   badge: "bg-slate-400 text-white",    ring: "ring-slate-300" },
  { bar: "#d97706", text: "text-orange-600",  badge: "bg-orange-500 text-white",   ring: "ring-orange-300" },
];
const DEFAULT_COLOR = { bar: "#B03858", text: "text-rose-700", badge: "bg-slate-100 text-slate-500", ring: "" };

function rankColor(i: number) {
  return RANK_COLORS[i] ?? DEFAULT_COLOR;
}

/* ─── Componente ─────────────────────────────────────────────────────────── */

interface Props {
  data:    CredorStat[];
  loading: boolean;
}

export function RankingCredores({ data, loading }: Props) {
  const [selected, setSelected] = useState<CredorStat | null>(null);

  const total   = data.reduce((s, d) => s + d.valor, 0);
  const top3pct = total > 0
    ? (data.slice(0, 3).reduce((s, d) => s + d.valor, 0) / total) * 100
    : 0;
  const maxVal  = data[0]?.valor ?? 1;

  /* insight: credor #1 */
  const top1    = data[0];
  const top1pct = total > 0 && top1 ? (top1.valor / total) * 100 : 0;

  return (
    <>
      <ChartContainer
        title="Ranking de Credores"
        subtitle="Fornecedores por valor total de despesas · Ranking completo"
        loading={loading}
        empty={!loading && data.length === 0}
        height="auto"
      >
        <div className="flex flex-col gap-4 pb-1">

          {/* ── Resumo ── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-[11px] text-slate-500 leading-tight">Total de despesas</p>
                <p className="text-base font-bold text-slate-800 leading-tight mt-0.5">
                  {formatCurrency(total, true)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
              <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                <PieChart className="w-4 h-4 text-brand-600" />
              </div>
              <div>
                <p className="text-[11px] text-slate-500 leading-tight">Top 3 credores concentram</p>
                <p className="text-base font-bold text-brand-700 leading-tight mt-0.5">
                  {formatNumber(top3pct, 0)}% do total
                </p>
              </div>
            </div>
          </div>

          {/* ── Cabeçalho da tabela ── */}
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 px-1
                          text-[10px] font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100 pb-1.5">
            <span className="w-6" />
            <span>Credor</span>
            <span className="text-right w-20">Valor (R$)</span>
            <span className="text-right w-14">% do total</span>
          </div>

          {/* ── Linhas (rolagem quando a lista for longa) ── */}
          <div className="min-h-0 max-h-[280px] overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable] pr-0.5 sm:max-h-[320px]">
            <div className="flex flex-col divide-y divide-slate-50">
              {data.map((item, i) => {
                const c       = rankColor(i);
                const pct     = total > 0 ? (item.valor / total) * 100 : 0;
                const barPct  = maxVal > 0 ? (item.valor / maxVal) * 100 : 0;
                const isTop3  = i < 3;

                return (
                  <button
                    key={item.name}
                    onClick={() => setSelected(item)}
                    className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 px-1 py-2.5
                               hover:bg-slate-50/80 rounded-lg transition-colors text-left w-full group"
                  >
                    {/* Posição */}
                    <span className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0",
                      c.badge,
                      isTop3 ? `ring-2 ${c.ring}` : ""
                    )}>
                      {i + 1}
                    </span>

                    {/* Nome + barra */}
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate leading-tight group-hover:text-slate-900">
                        {item.name}
                      </p>
                      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${barPct}%`, backgroundColor: c.bar }}
                        />
                      </div>
                    </div>

                    {/* Valor */}
                    <span className={cn(
                      "text-xs font-semibold w-20 text-right tabular-nums whitespace-nowrap",
                      isTop3 ? c.text : "text-slate-600"
                    )}>
                      {formatCurrency(item.valor, true)}
                    </span>

                    {/* % */}
                    <span className={cn(
                      "text-xs w-14 text-right tabular-nums",
                      isTop3 ? `font-bold ${c.text}` : "text-slate-400"
                    )}>
                      {formatNumber(pct, 1)}%
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Insight ── */}
          {top1 && (
            <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mt-1">
              <Lightbulb className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800 leading-snug">
                <span className="font-medium">Insight:</span>{" "}
                O credor{" "}
                <span className="font-semibold">{top1.name}</span>{" "}
                representa{" "}
                <span className="font-bold">{formatNumber(top1pct, 1)}%</span>{" "}
                do total de despesas.
              </p>
            </div>
          )}

        </div>
      </ChartContainer>

      {/* ── Modal de detalhes ── */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="xl"
        title={selected && (
          <ModalEntityTitle
            icon={<Users className="w-4 h-4" />}
            label={selected.name}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
        )}
      >
        {selected && (
          <>
            <SummaryBanner
              label="Total do Credor"
              value={formatCurrency(selected.valor)}
              color="#f59e0b"
              subtitle={`${formatNumber(selected.quantidade)} parcela${selected.quantidade !== 1 ? "s" : ""}`}
            />
            <OutcomeTable items={selected.items} />
          </>
        )}
      </Modal>
    </>
  );
}
